// Types utilitaires pour les composants Vue
import type { Graffiti, GraffitiFromAPI } from './entities'

// Re-export des entités pour faciliter l'import
export type { Graffiti, GraffitiFromAPI, GraffitiLien, Thematique } from './entities'

// Types enrichis pour l'affichage dans les composants
export interface GraffitiWithThemes extends Graffiti {
  themes: string[]
}

export interface GraffitiWithLinks extends GraffitiWithThemes {
  liens: GraffitiLienDisplay[]
}

export interface GraffitiLienDisplay {
  graffiti: Graffiti
  type: string
  degre: number
  commentaire: string
}

// Fonction utilitaire pour convertir les données API vers les types composants
export function convertGraffitiFromAPI(apiGraffiti: GraffitiFromAPI): GraffitiWithThemes {
  // Extraire les propriétés de base (sans themes, thematiques, liens)
  const { themes, thematiques: _thematiques, liens: _liens, ...baseGraffiti } = apiGraffiti

  // Si themes n'est pas défini, essayer de l'extraire de thematiques
  let themesArray: string[] = themes || []
  if (!themesArray.length && _thematiques && Array.isArray(_thematiques)) {
    themesArray = _thematiques
      .map((t: any) => (typeof t === 'string' ? t : t.thematique || t.theme || ''))
      .filter(Boolean)
  }

  // Retourner avec seulement le champ themes (string[])
  return {
    ...baseGraffiti,
    themes: themesArray,
  }
}

// Fonction pour convertir vers GraffitiWithLinks
export function convertGraffitiFromAPIWithLinks(apiGraffiti: GraffitiFromAPI): GraffitiWithLinks {
  const baseGraffiti = convertGraffitiFromAPI(apiGraffiti)

  // Convertir les liens du format API vers le format composant
  const liens: GraffitiLienDisplay[] =
    apiGraffiti.liens?.map(apiLink => ({
      graffiti: {
        id: 0,
        idGraf: apiLink.cible,
        idBdc: apiLink.cible_bdc,
        // Propriétés minimales pour satisfaire le type Graffiti - utiliser des valeurs par défaut
        mur: '',
        wallId: null,
        positionX: null,
        positionY: null,
        boundingBox: null,
        refLon: null,
        refLat: null,
        refHau: null,
        refRelOrdDeb: null,
        refRelOrdFin: null,
        refRelAbsDeb: null,
        refRelAbsFin: null,
        dimLon: null,
        dimHau: null,
        epaisseur: null,
        type: '',
        categorie: '',
        releve2017: false,
        releve2022: false,
        inedit: false,
        autopsie: false,
        localisationImprecise: false,
        datation: null,
        technique: null,
        support: null,
        biblio: null,
        dateFiche: null,
        langue: null,
        transcription: null,
        lecture: null,
        traduction: null,
        commentaire: null,
        description: null,
        photo: null,
        createdAt: '',
        updatedAt: '',
        createdBy: 0,
        updatedBy: null,
      },
      type: apiLink.type_lien,
      degre: apiLink.deg_lien,
      commentaire: apiLink.commentaire_lien || '',
    })) || []

  return {
    ...baseGraffiti,
    liens,
  }
}

// Types pour les composants de recherche
export interface SearchFilters {
  query?: string
  types?: string[]
  categories?: string[]
  thematiques?: number[]
  dateFrom?: string
  dateTo?: string
}

export interface SortOption {
  key: keyof Graffiti
  label: string
  direction: 'asc' | 'desc'
}

// Types pour les composants de visualisation
export interface ViewerState {
  zoom: number
  pan: { x: number; y: number }
  rotation: number
  selectedAnnotations: string[]
  visibleLayers: string[]
}

export interface AnnotationData {
  id: string
  graffitiId?: number
  type: 'graffiti' | 'lacune' | 'solin' | 'couleur' | 'trait'
  coordinates: number[][]
  style: AnnotationStyle
  metadata?: Record<string, unknown>
}

export interface AnnotationStyle {
  color: string
  strokeWidth: number
  fillOpacity: number
  strokeStyle: 'solid' | 'dashed' | 'dotted'
}

// Types pour les composants de navigation
export interface BreadcrumbItem {
  label: string
  path?: string
  active?: boolean
}

export interface MenuItem {
  id: string
  label: string
  icon?: string
  path?: string
  children?: MenuItem[]
  disabled?: boolean
  visible?: boolean
}

// Types pour les composants de données tabulaires
export interface TableColumn {
  key: string
  label: string
  sortable?: boolean
  width?: string
  align?: 'left' | 'center' | 'right'
  formatter?: (value: unknown) => string
}

export interface TableRow {
  id: string | number
  data: Record<string, unknown>
  selected?: boolean
  disabled?: boolean
}

// Types pour les formulaires
export interface FormField {
  key: string
  label: string
  type: 'text' | 'textarea' | 'select' | 'multiselect' | 'number' | 'date' | 'checkbox'
  required?: boolean
  options?: { value: unknown; label: string }[]
  validation?: (value: unknown) => string | null
}

export interface FormData {
  [key: string]: unknown
}

// Types pour les notifications/alertes
export interface NotificationItem {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
  actions?: NotificationAction[]
}

export interface NotificationAction {
  label: string
  action: () => void
  style?: 'primary' | 'secondary'
}

// --- Types spécifiques à une vue/un composant complexe ---

/**
 * Structure des données pour la vue de test de l'API (ApiTestView.vue)
 */
export interface ApiTestViewResults {
  health: import('./api').HealthCheckResponse | null
  stats: import('./api').StatsResponse | null
  graffitis: import('./api').ApiResponse<GraffitiFromAPI[]> | null
  detail: GraffitiFromAPI | null
  thematiques: import('./api').ThematiqueWithCountResponse | null
  search: import('./api').GraffitiSearchResponse | null
}
